// Investor module parser — converts admin-service snake_case wire DTOs to
// camelCase view-model types consumed everywhere else in the module.
// Only parsed data is allowed past this file; downstream code never reads
// snake_case fields.

import type {
  BankAccountDto,
  BankAccountListResponseDto,
  ComplianceActivityRowDto,
  ComplianceAmlSubmissionRowDto,
  ComplianceKycDetailsDto,
  ComplianceKycDocumentDto,
  ComplianceKycListItemDto,
  ComplianceKycListResponseDto,
  ComplianceKycSubmissionRowDto,
  CompliancePepSubmissionRowDto,
  InvestorAmlSummaryDto,
  InvestorDetailsDto,
  InvestorDto,
  InvestorListItemDto,
  InvestorListResponseDto,
  InvestorPepSummaryDto,
  KycDocumentDto,
  KycDto,
  PaginatedListResponseDto,
  PaginationMetaDto,
  StatusHistoryDto,
  InvestorOnboardingChecksDto,
  VaspListItemDto,
  VaspDetailsDto,
  VaspListResponseDto,
  VaspSubmitResponseDto,
} from './investorContracts'
import type {
  BankAccount,
  ComplianceActivityRow,
  ComplianceAmlSubmissionRow,
  ComplianceKycSubmissionRow,
  CompliancePepSubmissionRow,
  Investor,
  InvestorAmlSummary,
  InvestorDetails,
  InvestorListItem,
  InvestorListResponse,
  InvestorPepSummary,
  ComplianceKycListItem,
  ComplianceKycListResponse,
  BankAccountListResponse,
  Kyc,
  KycDocument,
  StatusHistory,
  ComplianceKycDetails,
  InvestorOnboardingChecks,
  VaspListItem,
  VaspDetails,
  VaspListResponse,
  VaspVerification,
  VaspApproveResult,
} from '../types/investorTypes'
import type { PaginatedResponse, PaginationMeta } from '@shared/types/pagination'
import type { VaspUserStatus } from '@shared/constants/vaspStatus'

// ─── MIME derivation (API omits mime_type; derive from file path/name) ──────

const EXT_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  mp4: 'video/mp4',
  webm: 'video/webm',
  ogg: 'video/ogg',
  mov: 'video/quicktime',
}

function deriveMimeType(filePath: string, fileName: string): string {
  const source = filePath || fileName
  try {
    const pathname = new URL(source).pathname
    const ext = pathname.split('.').pop()?.toLowerCase() ?? ''
    return EXT_MIME[ext] ?? ''
  } catch {
    const ext = source.split('.').pop()?.toLowerCase().split('?')[0] ?? ''
    return EXT_MIME[ext] ?? ''
  }
}

// ─── Per-entity parsers ─────────────────────────────────────────────────────

export const parseInvestorListItem = (dto: InvestorListItemDto): InvestorListItem => ({
  id: String(dto.id ?? dto.investorId ?? ''),
  firstName: String(dto.firstName ?? ''),
  lastName: String(dto.lastName ?? ''),
  email: String(dto.email ?? ''),
  phone: String(dto.phone ?? ''),
  investorType: String(dto.investorType ?? ''),
  investorStatus: String(dto.investorStatus ?? ''),
  accountState: (dto.accountState as InvestorListItem['accountState']) ?? null,
  joinedDate: String(dto.joinedDate ?? ''),
  kycStatus: String(dto.kycStatus ?? ''),
  bankAccountStatus: String(dto.bankAccountStatus ?? ''),
  totalInvestments: String(dto.totalInvestments ?? '0.00'),
  nationalityCountryName: String(dto.nationalityCountryName ?? ''),
  submittedAt: String(dto.submittedAt ?? ''),
  statusChangedAt: dto.statusChangedAt ?? undefined,
  statusChangedBy: dto.statusChangedBy ?? undefined,
})

export const parseComplianceKycListItem = (
  dto: ComplianceKycListItemDto,
): ComplianceKycListItem => ({
  id: String(dto.id ?? dto.kycId ?? ''),
  investorId: String(dto.investorId ?? ''),
  firstName: String(dto.firstName ?? ''),
  lastName: String(dto.lastName ?? ''),
  email: String(dto.email ?? ''),
  phone: String(dto.phone ?? ''),
  investorType: String(dto.investorType ?? ''),
  investorStatus: String(dto.investorStatus ?? ''),
  joinedDate: String(dto.joinedDate ?? ''),
  kycStatus: String(dto.kycStatus ?? ''),
  bankAccountStatus: String(dto.bankAccountStatus ?? ''),
  totalInvestments: String(dto.totalInvestments ?? '0.00'),
  nationalityCountryName: String(dto.nationalityCountryName ?? ''),
  submittedAt: String(dto.submittedAt ?? ''),
  expiresAt: dto.expiresAt ?? undefined,
})

export const parseVaspListItem = (dto: VaspListItemDto): VaspListItem => ({
  id: String(dto.id ?? ''),
  investorId: String(dto.investorId ?? ''),
  firstName: String(dto.firstName ?? ''),
  lastName: String(dto.lastName ?? ''),
  email: dto.email ?? undefined,
  vaspProvider: String(dto.vaspProvider ?? ''),
  vaspUserId: String(dto.vaspUserId ?? ''),
  // Wire value is a free-form string; the union is the anti-corruption seam
  // (an unknown value still renders — vaspStatusColor defaults to neutral).
  vaspUserStatus: String(dto.vaspUserStatus ?? '') as VaspUserStatus,
  vaspRejectionReason: dto.vaspRejectionReason ?? undefined,
  vaspRegisteredAt: String(dto.vaspRegisteredAt ?? ''),
  lastAttemptAt: String(dto.lastAttemptAt ?? ''),
  createdAt: String(dto.createdAt ?? ''),
})

const parseVaspVerification = (
  dto: NonNullable<VaspDetailsDto['verification']>,
): VaspVerification => ({
  type: String(dto.type ?? ''),
  status: String(dto.status ?? ''),
  createdAt: String(dto.createdAt ?? ''),
  lastCompletedAt: dto.lastCompletedAt ?? null,
  error: dto.error ?? null,
})

// admin-service is migrating this block from one record to the full trail.
// Read the array when it arrives and lift the singular into a one-item list
// otherwise, so the drawer only ever handles one shape. Delete the singular
// branch once every VASP detail response carries `verifications`.
const parseVaspVerifications = (dto: VaspDetailsDto): VaspVerification[] => {
  if (Array.isArray(dto.verifications)) return dto.verifications.map(parseVaspVerification)
  return dto.verification ? [parseVaspVerification(dto.verification)] : []
}

export const parseVaspDetails = (dto: VaspDetailsDto): VaspDetails => ({
  ...parseVaspListItem(dto),
  participantNumber: dto.participantNumber ?? null,
  thirdPartyIdentifier: dto.thirdPartyIdentifier ?? null,
  thirdPartyIdentifierType: dto.thirdPartyIdentifierType ?? null,
  verifications: parseVaspVerifications(dto),
  lastError: dto.lastError ?? null,
  lastErrorSource: dto.lastErrorSource ?? null,
  lastRequestId: dto.lastRequestId ?? null,
  retryCount: dto.retryCount ?? null,
  lastSyncedAt: dto.lastSyncedAt ?? null,
})

// The submit body, returned identically on a pass and — via the 409 error
// envelope — on a failed step. Kept lossless: `submitStepViews` in
// @shared/constants/vaspSubmit owns every label and fallback, so a step this
// parser has never seen still renders.
export const parseVaspSubmitResponse = (dto: VaspSubmitResponseDto): VaspApproveResult => ({
  id: String(dto.id ?? ''),
  vaspUserStatus: String(dto.vaspUserStatus ?? ''),
  steps: Array.isArray(dto.steps)
    ? dto.steps.map((entry) => ({
        step: entry.step ?? null,
        result: entry.result
          ? {
              status: entry.result.status ?? null,
              failureReason: entry.result.failureReason ?? null,
            }
          : null,
      }))
    : [],
})

export const parseInvestor = (dto: InvestorDto): Investor => ({
  id: String(dto.id ?? ''),
  authUserId: String(dto.authUserId ?? ''),
  investorType: String(dto.investorType ?? ''),
  firstName: String(dto.firstName ?? ''),
  lastName: String(dto.lastName ?? ''),
  email: String(dto.email ?? ''),
  phone: String(dto.phone ?? ''),
  dateOfBirth: String(dto.dateOfBirth ?? ''),
  nationalityCountryName: String(dto.nationalityCountryName ?? dto.nationalityCountryId ?? ''),
  marketName: String(dto.marketName ?? ''),
  activeKycId: String(dto.activeKycId ?? ''),
  onboardingStatus: String(dto.onboardingStatus ?? ''),
  profileCompleted: Boolean(dto.isProfileCompleted),
  isAccredited: Boolean(dto.isAccredited),
  investorStatus: String(dto.investorStatus ?? ''),
  addressLine1: dto.addressLine1 ?? null,
  addressLine2: dto.addressLine2 ?? null,
  city: dto.city ?? null,
  state: dto.stateProvince ?? null,
  postalCode: dto.postalCode ?? null,
  createdAt: String(dto.createdAt ?? ''),
  updatedAt: String(dto.updatedAt ?? ''),
})

export const parseKyc = (dto: KycDto): Kyc => ({
  id: String(dto.id ?? ''),
  entityType: String(dto.entityType ?? ''),
  entityId: String(dto.entityId ?? ''),
  kycType: String(dto.kycType ?? ''),
  kycVendor: String(dto.kycVendor ?? ''),
  vendorApplicantId: String(dto.vendorApplicantId ?? ''),
  kycStatus: String(dto.kycStatus ?? ''),
  rejectionReason: dto.rejectionReason ?? null,
  reviewNotes: String(dto.reviewNotes ?? ''),
  submittedAt: String(dto.submittedAt ?? ''),
  approvedAt: String(dto.approvedAt ?? ''),
  expiresAt: String(dto.expiresAt ?? ''),
  reviewedBy: String(dto.reviewedBy ?? ''),
  reviewedAt: String(dto.reviewedAt ?? ''),
  createdAt: String(dto.createdAt ?? ''),
  updatedAt: String(dto.updatedAt ?? ''),
  verificationChecks: (dto.verificationChecks ?? []).map((c) => ({
    name: String(c.name ?? ''),
    answer: c.answer ?? null,
    status: String(c.status ?? ''),
  })),
})

export const parseKycDocument = (dto: KycDocumentDto): KycDocument => {
  const filePath = String(dto.filePath ?? '')
  const fileName = String(dto.fileName ?? '')
  return {
    id: String(dto.id ?? ''),
    entityType: String(dto.entityType ?? ''),
    entityId: String(dto.entityId ?? ''),
    kycSubmissionId: String(dto.kycSubmissionId ?? ''),
    documentType: String(dto.documentType ?? ''),
    documentTitle: String(dto.documentTitle ?? ''),
    documentNumber: dto.documentNumber ?? null,
    filePath,
    fileName,
    fileSizeBytes: Number(dto.fileSizeBytes ?? 0),
    mimeType: String(dto.mimeType ?? deriveMimeType(filePath, fileName)),
    expiryDate: String(dto.expiryDate ?? ''),
    version: Number(dto.version ?? 0),
    isLatest: Boolean(dto.isLatest),
    documentStatus: String(dto.documentStatus ?? ''),
    rejectionReason: dto.rejectionReason ?? null,
    reviewedBy: String(dto.reviewedBy ?? ''),
    reviewedAt: String(dto.reviewedAt ?? ''),
    createdAt: String(dto.createdAt ?? ''),
  }
}

export const parseStatusHistory = (dto: StatusHistoryDto): StatusHistory => ({
  id: String(dto.id ?? ''),
  investorId: String(dto.investorId ?? ''),
  previousStatus: String(dto.previousStatus ?? ''),
  newStatus: String(dto.newStatus ?? ''),
  changeReason: String(dto.changeReason ?? ''),
  changedBy: String(dto.changedBy ?? ''),
  createdAt: String(dto.createdAt ?? ''),
})

export const parseBankAccount = (dto: BankAccountDto): BankAccount => {
  const investorRaw = dto.investor ?? ({} as BankAccountDto['investor'])
  const proofRaw = dto.proofDocument ?? null
  return {
    id: String(dto.id ?? ''),
    bankName: String(dto.bankName ?? ''),
    accountHolderName: String(dto.accountHolderName ?? ''),
    accountNumber: String(dto.accountNumber ?? ''),
    ifscCode: dto.ifscCode ?? null,
    iban: dto.iban ?? null,
    swiftCode: String(dto.swiftCode ?? ''),
    currencyCode: String(dto.currencyCode ?? ''),
    accountStatus: (dto.accountStatus ?? 'pending_verification') as BankAccount['accountStatus'],
    rejectionReason: dto.rejectionReason ?? null,
    verificationMethod: dto.verificationMethod,
    isPrimary: Boolean(dto.isPrimary),
    reviewedBy: dto.reviewedBy ?? null,
    reviewedAt: dto.reviewedAt ?? null,
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? '',
    investor: {
      id: String(investorRaw.id ?? ''),
      investorType: String(investorRaw.investorType ?? ''),
      firstName: String(investorRaw.firstName ?? ''),
      lastName: String(investorRaw.lastName ?? ''),
      email: investorRaw.email,
    },
    proofDocument: proofRaw
      ? {
          documentType: proofRaw.documentType ? String(proofRaw.documentType) : undefined,
          fileName: proofRaw.fileName ? String(proofRaw.fileName) : undefined,
          fileSizeBytes:
            proofRaw.fileSizeBytes != null ? Number(proofRaw.fileSizeBytes) : undefined,
          filePath: proofRaw.filePath ? String(proofRaw.filePath) : undefined,
          mimeType: proofRaw.mimeType ? String(proofRaw.mimeType) : undefined,
        }
      : null,
  }
}

export const parseInvestorAmlSummary = (dto: InvestorAmlSummaryDto): InvestorAmlSummary => ({
  id: String(dto.id ?? ''),
  amlStatus: String(dto.amlStatus ?? ''),
  amlType: String(dto.amlType ?? ''),
  amlVendor: String(dto.amlVendor ?? ''),
  vendorApplicantId: dto.vendorApplicantId ?? null,
  riskLevel: dto.riskLevel ?? null,
  riskScore: dto.riskScore ?? null,
  sanctionsMatch: dto.sanctionsMatch ?? null,
  adverseMediaMatch: dto.adverseMediaMatch ?? null,
  submittedAt: dto.submittedAt ?? null,
  approvedAt: dto.approvedAt ?? null,
  expiresAt: dto.expiresAt ?? null,
  reviewedBy: dto.reviewedBy ?? null,
  reviewedAt: dto.reviewedAt ?? null,
  reviewNotes: dto.reviewNotes ?? null,
})

export const parseInvestorPepSummary = (dto: InvestorPepSummaryDto): InvestorPepSummary => ({
  id: String(dto.id ?? ''),
  pepStatus: String(dto.pepStatus ?? ''),
  pepType: String(dto.pepType ?? ''),
  pepVendor: String(dto.pepVendor ?? ''),
  vendorApplicantId: dto.vendorApplicantId ?? null,
  matchStatus: dto.matchStatus ?? null,
  isPep: dto.isPep ?? null,
  matchedName: dto.matchedName ?? null,
  politicalPosition: dto.politicalPosition ?? null,
  submittedAt: dto.submittedAt ?? null,
  approvedAt: dto.approvedAt ?? null,
  expiresAt: dto.expiresAt ?? null,
  reviewedBy: dto.reviewedBy ?? null,
  reviewedAt: dto.reviewedAt ?? null,
  reviewNotes: dto.reviewNotes ?? null,
})

// Some legacy backends return the investor block flat at the root rather
// than under `dto.investor`, so the parser accepts both shapes. The
// `Partial<InvestorDto>` intersection encodes that fallback in the type
// rather than dropping back to `any`.
type InvestorDetailsResponseDto = InvestorDetailsDto & Partial<InvestorDto>

export const parseInvestorDetails = (dto: InvestorDetailsResponseDto): InvestorDetails => {
  const investorDto = dto.investor ?? (dto as InvestorDto)
  return {
    investor: parseInvestor(investorDto),
    kyc: dto.kyc ? parseKyc(dto.kyc) : ({} as Kyc),
    aml: dto.aml ? parseInvestorAmlSummary(dto.aml) : null,
    pep: dto.pep ? parseInvestorPepSummary(dto.pep) : null,
    kycDocuments: (dto.kycDocuments ?? []).map(parseKycDocument),
    bankAccountLinked: Boolean(dto.hasBankAccountLinked),
    bankAccounts: (dto.bankAccounts ?? []).map(parseBankAccount),
    vasp: dto.vasp ? parseVaspDetails(dto.vasp) : null,
    totalInvestedAmount: String(dto.totalInvestedAmount ?? '0.00'),
    isInvestmentEligible: Boolean(dto.isInvestmentEligible),
    statusHistory: (dto.statusHistory ?? []).map(parseStatusHistory),
    accountState: (dto.accountState as InvestorDetails['accountState']) ?? null,
    pausedReason: (dto.pausedReason as InvestorDetails['pausedReason']) ?? null,
  }
}

// ─── Compliance history row parsers (KYC / AML / PEP submissions) ──────────

export const parseComplianceKycSubmissionRow = (
  dto: ComplianceKycSubmissionRowDto,
): ComplianceKycSubmissionRow => ({
  id: String(dto.id ?? ''),
  kycStatus: String(dto.kycStatus ?? ''),
  kycType: String(dto.kycType ?? ''),
  kycVendor: String(dto.kycVendor ?? ''),
  vendorApplicantId: dto.vendorApplicantId ?? null,
  submittedAt: dto.submittedAt ?? null,
  approvedAt: dto.approvedAt ?? null,
  expiresAt: dto.expiresAt ?? null,
  reviewedBy: dto.reviewedBy ?? null,
  reviewedAt: dto.reviewedAt ?? null,
  reviewNotes: dto.reviewNotes ?? null,
  rejectionReason: dto.rejectionReason ?? null,
  isActive: Boolean(dto.isActive ?? false),
})

export const parseComplianceAmlSubmissionRow = (
  dto: ComplianceAmlSubmissionRowDto,
): ComplianceAmlSubmissionRow => ({
  id: String(dto.id ?? ''),
  amlStatus: String(dto.amlStatus ?? ''),
  amlType: String(dto.amlType ?? ''),
  amlVendor: String(dto.amlVendor ?? ''),
  vendorApplicantId: dto.vendorApplicantId ?? null,
  riskLevel: dto.riskLevel ?? null,
  riskScore: dto.riskScore ?? null,
  sanctionsMatch: dto.sanctionsMatch ?? null,
  adverseMediaMatch: dto.adverseMediaMatch ?? null,
  submittedAt: dto.submittedAt ?? null,
  approvedAt: dto.approvedAt ?? null,
  expiresAt: dto.expiresAt ?? null,
  reviewedBy: dto.reviewedBy ?? null,
  reviewedAt: dto.reviewedAt ?? null,
  reviewNotes: dto.reviewNotes ?? null,
  isActive: Boolean(dto.isActive ?? false),
})

export const parseCompliancePepSubmissionRow = (
  dto: CompliancePepSubmissionRowDto,
): CompliancePepSubmissionRow => ({
  id: String(dto.id ?? ''),
  pepStatus: String(dto.pepStatus ?? ''),
  pepType: String(dto.pepType ?? ''),
  pepVendor: String(dto.pepVendor ?? ''),
  vendorApplicantId: dto.vendorApplicantId ?? null,
  matchStatus: dto.matchStatus ?? null,
  isPep: dto.isPep ?? null,
  matchedName: dto.matchedName ?? null,
  politicalPosition: dto.politicalPosition ?? null,
  submittedAt: dto.submittedAt ?? null,
  approvedAt: dto.approvedAt ?? null,
  expiresAt: dto.expiresAt ?? null,
  reviewedBy: dto.reviewedBy ?? null,
  reviewedAt: dto.reviewedAt ?? null,
  reviewNotes: dto.reviewNotes ?? null,
  isActive: Boolean(dto.isActive ?? false),
})

export const parseComplianceActivityRow = (
  dto: ComplianceActivityRowDto,
): ComplianceActivityRow => ({
  id: String(dto.id ?? ''),
  complianceType: String(dto.complianceType ?? ''),
  eventType: String(dto.eventType ?? ''),
  actorType: String(dto.actorType ?? ''),
  actorId: dto.actorId ?? null,
  oldValue: dto.oldValue ?? null,
  newValue: dto.newValue ?? null,
  notes: dto.notes ?? null,
  createdAt: String(dto.createdAt ?? ''),
})

// ─── Canonical paginated parser — `{ data, meta }` → PaginatedResponse<T> ──
// Use for the new compliance endpoints. Synthesizes totalPages when the
// server omits it. The existing list parsers (parseInvestorListResponse,
// parseComplianceKycListResponse) emit the legacy `{ rows, rowsCount }`
// shape consumed by DataTableV2 wrappers across the module — left intact
// until those migrate.

function readPaginationMeta(
  meta: PaginationMetaDto | undefined,
  fallbackLength: number,
): PaginationMeta {
  const limit = Number((meta?.limit ?? fallbackLength) || 20)
  const totalItems = Number(meta?.totalItems ?? meta?.total ?? fallbackLength)
  const page = Number(meta?.page ?? 1)
  const totalPages = Number(
    meta?.totalPages ?? (limit > 0 ? Math.max(1, Math.ceil(totalItems / limit)) : 1),
  )
  return { page, limit, totalItems, totalPages }
}

export function parsePaginatedResponse<TDto, TView>(
  dto: PaginatedListResponseDto<TDto> | null | undefined,
  parseRow: (row: TDto) => TView,
): PaginatedResponse<TView> {
  const rows = dto?.data ?? []
  return {
    data: rows.map(parseRow),
    pagination: readPaginationMeta(dto?.meta, rows.length),
  }
}

export const parseInvestorKycSubmissionsResponse = (
  dto: PaginatedListResponseDto<ComplianceKycSubmissionRowDto>,
): PaginatedResponse<ComplianceKycSubmissionRow> =>
  parsePaginatedResponse(dto, parseComplianceKycSubmissionRow)

export const parseInvestorAmlSubmissionsResponse = (
  dto: PaginatedListResponseDto<ComplianceAmlSubmissionRowDto>,
): PaginatedResponse<ComplianceAmlSubmissionRow> =>
  parsePaginatedResponse(dto, parseComplianceAmlSubmissionRow)

export const parseInvestorPepSubmissionsResponse = (
  dto: PaginatedListResponseDto<CompliancePepSubmissionRowDto>,
): PaginatedResponse<CompliancePepSubmissionRow> =>
  parsePaginatedResponse(dto, parseCompliancePepSubmissionRow)

export const parseInvestorComplianceActivityResponse = (
  dto: PaginatedListResponseDto<ComplianceActivityRowDto>,
): PaginatedResponse<ComplianceActivityRow> =>
  parsePaginatedResponse(dto, parseComplianceActivityRow)

// ─── List-envelope parsers ──────────────────────────────────────────────────

// Backend now returns the treasury-shaped `{ data, meta }` envelope from the
// canonical /admin/investors endpoint. We keep tolerant fallbacks for the
// legacy {rows, rowsCount} shape because the compliance, VASP, and bank
// list endpoints still emit it. The view-model stays {rows, rowsCount} for
// existing consumers (DataTableV2 wrappers) until those switch to {data, meta}.

type LegacyOrEnveloped<T> =
  | { data?: T[]; meta?: { total?: number; totalItems?: number } }
  | { rows?: T[]; rowsCount?: number; total?: number }

function readListRows<T, V>(
  raw: LegacyOrEnveloped<T> | null | undefined,
  parseRow: (row: T) => V,
): { rows: V[]; rowsCount: number } {
  const envelope = raw ?? {}
  const enveloped = envelope as { data?: T[]; meta?: { total?: number; totalItems?: number } }
  const legacy = envelope as { rows?: T[]; rowsCount?: number; total?: number }

  const source = enveloped.data ?? legacy.rows ?? []
  const total =
    enveloped.meta?.total ?? enveloped.meta?.totalItems ?? legacy.rowsCount ?? legacy.total ?? 0

  return {
    rows: source.map(parseRow),
    rowsCount: Number(total),
  }
}

export const parseInvestorListResponse = (dto: InvestorListResponseDto): InvestorListResponse =>
  readListRows(dto, parseInvestorListItem)

export const parseComplianceKycListResponse = (
  dto: ComplianceKycListResponseDto,
): ComplianceKycListResponse => readListRows(dto, parseComplianceKycListItem)

export const parseVaspListResponse = (dto: VaspListResponseDto): VaspListResponse =>
  readListRows(dto, parseVaspListItem)

export const parseBankAccountListResponse = (
  dto: BankAccountListResponseDto,
): BankAccountListResponse => readListRows(dto, parseBankAccount)

export const parseComplianceKycDetails = (dto: ComplianceKycDetailsDto): ComplianceKycDetails => ({
  kycId: String(dto.kycId ?? ''),
  investorId: String(dto.investorId ?? ''),
  kycStatus: String(dto.kycStatus ?? ''),
  submittedAt: String(dto.submittedAt ?? ''),
  reviewNotes: dto.reviewNotes ?? null,
  rejectionReason: dto.rejectionReason ?? null,
  marketName: String(dto.marketName ?? ''),
  riskLevel: String(dto.riskLevel ?? ''),
  overallComplianceStatus: String(dto.overallComplianceStatus ?? ''),
  isAccredited: Boolean(dto.isAccredited),
  profile: {
    firstName: String(dto.profile?.firstName ?? ''),
    lastName: String(dto.profile?.lastName ?? ''),
    email: String(dto.profile?.email ?? ''),
    dateOfBirth: String(dto.profile?.dateOfBirth ?? ''),
    nationality: String(dto.profile?.nationality ?? ''),
    phone: String(dto.profile?.phone ?? ''),
    investorType: String(dto.profile?.investorType ?? ''),
    signedUpAt: String(dto.profile?.signedUpAt ?? ''),
  },

  documents: (dto.documents ?? []).map((doc: ComplianceKycDocumentDto) => ({
    id: String(doc.id ?? ''),
    documentType: String(doc.documentType ?? ''),
    documentTitle: String(doc.documentTitle ?? ''),
    filePath: String(doc.filePath ?? ''),
    fileName: String(doc.fileName ?? ''),
    expiryDate: String(doc.expiryDate ?? ''),
    documentStatus: String(doc.documentStatus ?? ''),
    mimeType: String(doc.mimeType ?? deriveMimeType(doc.filePath ?? '', doc.fileName ?? '')),
    rejectionReason: doc.rejectionReason ?? null,
  })),
  address: {
    addressLine1: String(dto.address?.addressLine1 ?? ''),
    addressLine2: dto.address?.addressLine2 ?? null,
    city: String(dto.address?.city ?? ''),
    stateProvince: String(dto.address?.stateProvince ?? ''),
    postalCode: String(dto.address?.postalCode ?? ''),
    countryName: String(dto.address?.countryName ?? ''),
  },
  idVerificationStatus: String(dto.idVerificationStatus ?? ''),
  faceLivelinessStatus: String(dto.faceLivelinessStatus ?? ''),
  addressVerificationStatus: String(dto.addressVerificationStatus ?? ''),
  idVerificationReason: dto.idVerificationReason ?? null,
  faceLivelinessReason: dto.faceLivelinessReason ?? null,
  addressVerificationReason: dto.addressVerificationReason ?? null,
})

export function parseInvestorOnboardingChecks(
  dto: InvestorOnboardingChecksDto,
): InvestorOnboardingChecks {
  return {
    kycId: dto.kycId,
    amlStatus: dto.amlStatus ?? null,
    pepStatus: dto.pepStatus ?? null,
    onboardingSignals: { ...dto.onboardingSignals },
    investor: { ...dto.investor },
  }
}
